import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, firstValueFrom, lastValueFrom } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ProtoUrl } from 'src/app/@shared/protoUrl';
import { ClientFilterObject } from '@common/filter.decorator';
import { ContentOptions, ContentService } from 'src/app/@core/content.service';
import { StorageService } from 'src/app/@core/storage.service';

export type ClassificationKind = 'domain' | 'icdArea' | 'icdGroup';
export type ClassificationChange = { kind: ClassificationKind; type: 'create' | 'rename' | 'delete' };
export interface ClassificationEntry { id: string; title: string; topicCount: number; }
export interface ClassificationSource { id: string; title: string; multiFieldSearch?: string; }
export const CLASSIFICATION_LABELS: Record<ClassificationKind, string> = {
  domain: 'MENU.ITEMS.TOPIC_DOMAINS.TITLE',
  icdArea: 'MENU.ITEMS.ICD_CHAPTERS.TITLE',
  icdGroup: 'MENU.ITEMS.ICD_GROUPS.TITLE',
};
const CHOICES_MAX_AGE_MS = 60_000;

@Injectable({ providedIn: 'root' })
export class ClassificationService extends ContentService<ClassificationEntry, ClassificationSource> {
  readonly changed = new Subject<ClassificationChange>();
  private http = inject(HttpClient);
  private storage = inject(StorageService);
  private requests: Record<string, number> = {};
  private choicesLoadedAt: Record<string, number> = {};
  private choicesLoading: Record<string, Promise<ClassificationEntry[]>> = {};
  private choiceAccessToken: string | null = null;
  private choiceScope = 0;

  static getListId(kind: ClassificationKind, search = ''): string {
    return `${kind}:${search.trim().toLocaleLowerCase('de')}`;
  }

  override loadContent(id: string, options: ContentOptions<ClassificationSource> = {}): Observable<ClassificationEntry[]> {
    const choices = id.startsWith('choices:');
    const kind = (choices ? id.split(':').at(-1) : id.split(':', 1)[0]) as ClassificationKind;
    const filter: ClientFilterObject<ClassificationSource> = this.getNormalizedFilter(id, options.filter);
    const request = (this.requests[id] || 0) + 1;
    this.requests[id] = request;
    const url = choices ? this.url(kind) : ProtoUrl.getUrl(`topic-classifications/${kind}`, { filter });

    return this.http.get<ClassificationEntry[]>(url, {
      observe: 'response',
    }).pipe(
      map(response => ({ items: response.body || [], totalCount: Number(response.headers.get('x-total-count')) })),
      tap(({ items, totalCount }) => {
        if (request === this.requests[id]) {
          this.setItems(items, id, choices ? { skip: 0 } : filter, choices ? items.length : totalCount);
          if (choices) this.choicesLoadedAt[id] = Date.now();
        }
      }),
      map(({ items }) => items),
    );
  }

  async list(kind: ClassificationKind): Promise<ClassificationEntry[]> {
    const accessToken = this.storage.get('accessToken', null) as string | null;
    if (accessToken !== this.choiceAccessToken) {
      this.choiceAccessToken = accessToken;
      this.choiceScope++;
    }
    const id = this.choiceScope ? `choices:${this.choiceScope}:${kind}` : `choices:${kind}`;
    if (this.choicesLoading[id]) return this.choicesLoading[id];
    const loadedAt = this.choicesLoadedAt[id];
    const forceReload = loadedAt !== undefined && Date.now() - loadedAt >= CHOICES_MAX_AGE_MS;
    const loading = firstValueFrom(this.getContent(id, { type: 'choices', forceReload })).then((items) => {
      if (this.getMetaData(id).error) throw this.getMetaData(id).error;
      if (!items) throw new Error('Classification choices unavailable');
      return items;
    });
    this.choicesLoading[id] = loading;
    try { return await loading; } finally {
      if (this.choicesLoading[id] === loading) delete this.choicesLoading[id];
    }
  }

  async save(kind: ClassificationKind, title: string, id?: string): Promise<void> {
    await lastValueFrom(id ? this.http.patch(`${this.url(kind)}/${id}`, { title }) : this.http.post(this.url(kind), { title }));
    this.invalidate(kind);
    this.changed.next({
      kind,
      type: id
        ? 'rename'
        : 'create',
    });
  }

  async delete(kind: ClassificationKind, id: string): Promise<void> {
    await lastValueFrom(this.http.delete(`${this.url(kind)}/${id}`));
    this.invalidate(kind);
    this.changed.next({
      kind,
      type: 'delete',
    });
  }

  private invalidate(kind: ClassificationKind): void {
    for (const id of Object.keys(this.items)) {
      if (!(id.startsWith('choices:') && id.endsWith(`:${kind}`)) && id !== `choices:${kind}`
        && !id.startsWith(`${kind}:`)) continue;
      this.items[id].next(null);
      delete this.itemsStore[id];
      delete this.metadataStore[id];
      delete this.choicesLoadedAt[id];
      delete this.choicesLoading[id];
      this.requests[id] = (this.requests[id] || 0) + 1;
    }
  }

  private url(kind: ClassificationKind): string { return ProtoUrl.getUrl(`topic-classifications/${kind}`); }
}
